import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink } from "lucide-react";
import project1 from "../assets/project1.png";
import project2 from "../assets/project2.png";
import project3 from "../assets/project3.png";
import project4 from "../assets/project4.png";
import project5 from "../assets/project5.png";
import project6 from "../assets/project6.png";
import project7 from "../assets/project7.png";
import project8 from "../assets/project8.png";

const projectsData = [
  {
    id: 9,
    title: "Envipro Water Monitoring System (PT Quantum Prima Solusi)",
    description: [
      "Developed a web-based IoT wastewater monitoring platform integrated with the KLHK SPARING reporting system for automated environmental compliance.",
      "Built real-time dashboards, device monitoring, sensor data logging, and interactive analytics for environmental monitoring.",
      "Implemented reliable data synchronization, audit logging, and dispatch retry mechanisms to ensure successful regulatory data transmission."
    ],
    image: "/envipro.jpg",
    category: "IoT System",
    year: "2026",
    role: "Full-Stack Developer",
  },
  {
    id: 10,
    title: "Distribution Management System (Utama Motor)",
    description: [
      "Developed an enterprise distribution management system integrating purchasing, inventory, sales, and finance.",
      "Automated business workflows, inventory control, and role-based access management to improve operational efficiency."
    ],
    image: "/utama-motor.jpeg",
    category: "Enterprise System",
    year: "2025",
    role: "Full-Stack Developer",
  },
  {
    id: 1,
    title:
      "Corporate Website APBISDI (Asosiasi Profesi dan Pendidikan Bisnis Digital Indonesia)",
    description:
      "Development of the official website for APBISDI, the national association for Business Digital professionals and educators. The project focused on delivering a modern, responsive, and information-driven platform to support organizational communication, membership registration, activity documentation, and publication of association news.",
    image: project8,
    category: "Organization Website",
    year: "2025",
    role: "Web Developer",
    links: [
      {
        label: "View Project",
        url: "https://apbisdi.com",
      },
    ],
  },
  {
    id: 2,
    title: "360-Degree Employee Assessment Management System",
    description:
      "Comprehensive employee evaluation system for PT Bina Potensia Indonesia. Designed and developed a complete assessment platform including requirements analysis, system architecture, UI/UX design, and full-stack implementation with advanced reporting capabilities.",
    image: project1,
    category: "Enterprise System",
    year: "2025",
    role: "Full-Stack Developer",
    links: [
      {
        label: "View Project",
        url: "https://360degree-binapotensiaindonesia.com",
      },
    ],
  },
  {
    id: 3,
    title: "Corporate Website PT Panca Tunggal Buana",
    description:
      "Professional corporate website development featuring modern design principles, responsive layout, and content management capabilities. Conducted comprehensive requirements analysis and delivered a scalable web solution.",
    image: project2,
    category: "Web/Frontend",
    year: "2024",
    role: "Full-Stack Developer",
    links: [
      {
        label: "View Project",
        url: "https://pancatunggalbuana.com",
      },
    ],
  },
  {
    id: 4,
    title: "SILAKSAK: MSME Financial Reporting System",
    description:
      "Progressive Web Application (PWA) for enhancing MSMEs through SAK-based financial reporting system. Developed under Ministry of Education Research Grant, featuring comprehensive financial management tools and compliance reporting.",
    image: project3,
    category: "Research Project",
    year: "2024",
    role: "Full-Stack Developer",
    links: [
      {
        label: "Research Paper",
        url: "https://discovery.researcher.life/article/silaksak-utilization-as-support-sak-based-msme-financial-digital-records/f6d4676b2e7d34f9987296cd8ade8d9c",
      },
    ],
  },
  {
    id: 5,
    title: "Wedding Vendor Platform Garut",
    description:
      "Comprehensive web-based platform connecting wedding vendors across Garut region. Developed in collaboration with University of Garut, featuring vendor management, booking system, and client interaction tools.",
    image: project4,
    category: "Platform Development",
    year: "2024",
    role: "Full-Stack Developer",
  },
  {
    id: 6,
    title: "E-Clinic SO Medika Pangandaran",
    description:
      "Comprehensive web-based clinic management system integrated with Indonesia's national health platform (SatuSehat). Developed in partnership with IDI Pangandaran, featuring patient management, medical records, and health ministry integration.",
    image: project5,
    category: "Healthcare System",
    year: "2024",
    role: "Backend Developer",
    links: [
      {
        label: "View Project",
        url: "https://somedika.com/login",
      },
    ],
  },
  {
    id: 7,
    title: "Expedition Management System CV Merpati Jaya Express",
    description:
      "Ongoing development of comprehensive logistics and expedition management system. Features include package tracking, route optimization, customer management, and real-time delivery monitoring capabilities.",
    image: project7,
    category: "Logistics System",
    year: "2024",
    role: "Full-Stack Developer",
    links: [
      {
        label: "View Project",
        url: "https://merpati.jayaoffice.com",
      },
    ],
  },
  {
    id: 8,
    title: "Mobile MSME Financial Monitoring System (SimpanKas)",
    description:
      "Research-based mobile application for MSME financial monitoring and management. Developed under Ministry of Education Research Grant, focusing on accessible financial tools for small and medium enterprises.",
    image: project6,
    category: "Research Project",
    year: "2023",
    role: "Full-Stack Developer",
    links: [
      {
        label: "Research Paper",
        url: "https://jumanji.unjani.ac.id/index.php/jumanji/article/download/322/73",
      },
    ],
  },
];

const Projects = () => {
  const [visibleCount, setVisibleCount] = useState(4);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 4);
  };

  return (
    <section id="project" className="py-24 px-6 md:px-20 bg-background text-primary relative">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-sm font-semibold tracking-widest text-muted uppercase mb-2">03. Work</h2>
          <div className="w-12 h-1 bg-accent/50 rounded-full mb-8"></div>
          <h3 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
            Selected <span className="text-accent font-medium">Projects</span>.
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <AnimatePresence>
            {projectsData.slice(0, visibleCount).map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (index % 2) * 0.2 }}
                className="group relative rounded-3xl bg-surface/30 border border-white/5 overflow-hidden flex flex-col hover:border-white/10 transition-colors"
              >
                {/* Image Section */}
                <div className="relative h-64 overflow-hidden bg-black border-b border-white/5">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10 duration-500"></div>
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transform group-hover:scale-105 transition-all duration-700 ease-out"
                  />
                  <div className="absolute top-4 left-4 z-20">
                    <span className="text-xs px-3 py-1.5 bg-black/60 backdrop-blur-md text-white rounded-full font-medium border border-white/10">
                      {project.category}
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8 flex flex-col flex-grow">
                  <h4 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">
                    {project.title}
                  </h4>
                  
                  <div className="text-muted/80 text-sm leading-relaxed mb-6 flex-grow">
                    {Array.isArray(project.description) ? (
                      <ul className="list-disc pl-4 space-y-1.5 marker:text-accent/70">
                        {project.description.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{project.description}</p>
                    )}
                  </div>

                  <div className="mt-auto space-y-4 pt-4 border-t border-white/5">
                    <div className="flex justify-between text-sm text-muted">
                      <span>{project.role}</span>
                      <span>{project.year}</span>
                    </div>

                    {project.links && (
                      <div className="flex gap-4 pt-2">
                        {project.links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent transition-colors"
                          >
                            {link.label}
                            <ExternalLink size={14} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {visibleCount < projectsData.length && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 flex justify-center"
          >
            <button
              onClick={handleLoadMore}
              className="px-8 py-3 bg-surface border border-white/10 text-primary font-medium rounded-full hover:bg-white/5 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Load More Projects
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Projects;
